import React, { useState } from 'react';
import { useTheme, Box, Typography, styled, Avatar, Checkbox } from '@mui/material';
import Button from 'components/Common/Button';
import Grid from '@mui/material/Grid2';
import { Controller, useForm } from 'react-hook-form';
import Input from 'components/Common/Input';
import { useGetBusinessProfile, useUpdateBusinessProfile } from 'apis/business/hook';
import Select from 'components/Common/Select';
import { useGetCountries, useGetStates } from 'apis/country/hook';
import { useCreateBusiness, useGetBusiness, useUpdateBusiness } from 'apis/agency/hook';
import Switch from 'components/Common/Switch';
import TelInput from 'components/Common/TelInput';
import PlanCard from '@/components/Common/PlanCard';
import BusinessProfileLoader from './components/Loader';
import BusinessStatusSelect from './components/BusinessStatusSelect';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GoChevronLeft } from 'react-icons/go';
import { useGetBusinessPackages } from '@/apis/agency/businessPackages/hook';
import VoiceEngineCard from '@/pages/Agency/VoiceEngines/components/VoiceEngineCard';
import { useGetVoiceEngines } from '@/apis/agency/voiceEngines/hook';
import { useGetIndustries } from '@/apis/industries/hook';
import { useGetTelephonyProviders } from 'apis/agency/telephony/hook';

const normalizeProviderIds = (value: unknown): number[] => {
  if (Array.isArray(value)) return value.map((item) => Number(item)).filter(Boolean);
  if (typeof value === 'number') return [value];
  return [];
};

export default function BusinessProfileForm({ agencyView }: { agencyView: Boolean }) {
  const { businessId } = useParams();

  const theme = useTheme();
  const navigate = useNavigate();

  const location = useLocation();
  const isProfileRoute = location.pathname === '/setting/profile';

  const isCreateMode = !businessId && !isProfileRoute;
  const agencyEditId = agencyView && businessId ? Number(businessId) : 0;

  const { data: agencyBusiness, isLoading: agencyBusinessLoading } = useGetBusiness(
    agencyEditId,
    { include: 'telephony_providers,address' },
    { enabled: Boolean(agencyEditId) }
  );
  const { data: businessWorkspaceProfile, isLoading: businessProfileLoading } = useGetBusinessProfile(
    { include: 'address' },
    { enabled: !isCreateMode && !agencyView } as any
  );
  const businessProfile = agencyView ? agencyBusiness : businessWorkspaceProfile;
  const isLoading = agencyView ? agencyBusinessLoading : businessProfileLoading;
  const { data: industries, isLoading: loadingIndustries } = useGetIndustries();

  const { mutateAsync: createBusiness, isPending: isCreating } = useCreateBusiness(() => navigate(-1));

  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    businessProfile?.avatar_url ? businessProfile?.avatar_url : null
  );

  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const { mutateAsync: updateBusinessProfile, isPending: isUpdatingProfile } = useUpdateBusinessProfile();
  const { mutateAsync: updateBusiness, isPending: isUpdatingBusiness } = useUpdateBusiness(() => navigate(-1));

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    const newUrl = URL.createObjectURL(file);
    setAvatarUrl(newUrl);

    if (!isCreateMode) {
      // edit mode → instant upload
      const formData = new FormData();
      formData.append('business[avatar]', file);
      await updateBusinessProfile(formData);
    } else {
      // create mode → store file
      setAvatarFile(file);
    }
  };

  const {
    handleSubmit,
    control,
    watch,
    setError,
    formState: { dirtyFields, errors }
  } = useForm({
    defaultValues: { business: { account_status: 'active' } },
    values: !isCreateMode
      ? {
          business: {
            ...businessProfile,
            address_attributes: businessProfile?.address
          }
        }
      : {}
  });
  const businessCountry = watch('business.address_attributes.country');
  const { data: countries, isLoading: isCountriesLoading } = useGetCountries();
  const { data: states, isLoading: isStatesLoading } = useGetStates(businessCountry);
  const { data: plans } = useGetBusinessPackages();
  const { data: voiceEngines } = useGetVoiceEngines();
  const { data: telephonyProviders } = useGetTelephonyProviders(undefined, { enabled: Boolean(agencyView) });

  const onSubmit = async (data: any) => {
    if (isCreateMode) {
      const planId = data.business?.business_plan_id;
      if (planId === '' || planId === undefined || planId === null) {
        setError('business.business_plan_id', {
          type: 'manual',
          message: 'Please select a package'
        });
        return;
      }

      const formData = new FormData();

      Object.keys(data.business).forEach((key) => {
        const value = data.business[key];
        if (Array.isArray(value)) {
          value.forEach((item) => {
            formData.append(`business[${key}][]`, item);
          });
          return;
        }

        if (key === 'address_attributes' && value && typeof value === 'object') {
          Object.entries(value).forEach(([attrKey, attrVal]) => {
            if (attrVal != null && attrVal !== '') {
              formData.append(`business[address_attributes][${attrKey}]`, String(attrVal));
            }
          });
          return;
        }

        if (value != null && value !== '' && typeof value !== 'object') {
          formData.append(`business[${key}]`, value);
        }
      });

      if (avatarFile) {
        formData.append('business[avatar]', avatarFile);
      }

      await createBusiness(formData);
      return;
    }

    // EDIT MODE
    if (agencyView) {
      await updateBusiness(data);
    } else {
      await updateBusinessProfile(data);
    }
  };

  if (businessId && isLoading) {
    return <BusinessProfileLoader />;
  }

  return (
    <>
      <Box display='flex' alignItems='start' gap={1} sx={{ width: '100%', overflowX: 'hidden' }}>
        {agencyView && (
          <Box
            onClick={() => navigate(-1)}
            display='flex'
            alignItems='center'
            justifyContent='center'
            border={`1px solid ${theme.palette.primary.light}`}
            borderRadius='50%'
            mt={0.6}
            width={22}
            height={22}
            sx={{ cursor: 'pointer', bgcolor: '#fff', flexShrink: 0 }}
          >
            <GoChevronLeft color={theme.palette.primary.main} />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant='h6' fontWeight={500} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
            {isCreateMode ? 'Add New Business' : 'Business Profile'}
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '14px', lg: 16 } }} mt={0.5}>
            {agencyView ? 'Manage business profile information and settings' : 'Manage business profile information'}
          </Typography>
        </Box>
      </Box>
      <Box component='form' onSubmit={handleSubmit(onSubmit)} mt={2} sx={{ width: '100%', overflowX: 'hidden' }}>
        <Grid container spacing={{ xs: 2, lg: 3 }}>
          {agencyView && (
            <Grid
              size={{ xs: 12 }}
              bgcolor={theme.palette.background.paper}
              padding={{ xs: '16px', lg: '24px' }}
              borderRadius='8px'
              border='1px solid #ececec'
            >
              <Box
                display='flex'
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={{ xs: 2, sm: 0 }}
              >
                <Box display='flex' flexDirection='column' gap={0.5} sx={{ flex: 1 }}>
                  <Typography variant='body1' sx={{ fontSize: { xs: '1rem', lg: 20 } }} fontWeight={500}>
                    Account Status
                  </Typography>
                  <Typography variant='body2' sx={{ fontSize: { xs: '14px', lg: 16 } }} color='secondary'>
                    Enable or disable this business account. Disabled accounts cannot access the system.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Box sx={{ minWidth: 220 }}>
                    <BusinessStatusSelect
                      control={control}
                      name='business.account_status'
                      businessName={businessProfile?.name ?? ''}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>
          )}
          {agencyView && (
            <Grid
              size={{ xs: 12 }}
              bgcolor={theme.palette.background.paper}
              padding={{ xs: '16px', lg: '24px' }}
              borderRadius='8px'
              border='1px solid #ececec'
            >
              <Box
                display='flex'
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={{ xs: 2, sm: 0 }}
              >
                <Box display='flex' flexDirection='column' gap={0.5} sx={{ flex: 1 }}>
                  <Typography variant='body1' sx={{ fontSize: { xs: '1rem', lg: 20 } }} fontWeight={500}>
                    Allow Agent Behavior Changes
                  </Typography>
                  <Typography variant='body2' sx={{ fontSize: { xs: '14px', lg: 16 } }} color='secondary'>
                    Enable this to let your business modify how the agent behaves.
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Switch name='business.behaviour_editable' control={control} />
                </Box>
              </Box>
            </Grid>
          )}
          <Grid
            size={12}
            bgcolor={theme.palette.background.paper}
            padding={{ xs: '16px', lg: '24px' }}
            borderRadius='8px'
            border='1px solid #ececec'
          >
            <Typography variant='h6' marginBottom={2} sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
              General Information
            </Typography>

            <Box
              sx={{
                border: '1px solid #ececec',
                borderRadius: '8px',
                padding: { xs: 1.5, lg: 2 }
              }}
            >
              <Typography variant='body1' fontWeight='500' sx={{ fontSize: { xs: '0.875rem', lg: '1rem' } }}>
                Business Logo
              </Typography>
              <Box
                display={{ xs: 'block', sm: 'flex' }}
                sx={{
                  gap: { xs: 1.5, lg: 2 },
                  alignItems: 'center',
                  marginTop: '8px'
                }}
              >
                <Avatar
                  src={avatarUrl || '/path/to/default/avatar.png'}
                  alt='User Avatar'
                  sx={{ width: { xs: 56, lg: 64 }, height: { xs: 56, lg: 64 } }}
                />
                <Box sx={{ mt: { xs: 1.5, sm: 0 } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 1, sm: '8px' },
                      alignItems: { xs: 'flex-start', sm: 'center' }
                    }}
                  >
                    <Button component='label' role={undefined} variant='outlined' tabIndex={-1} size='small'>
                      Choose files
                      <VisuallyHiddenInput type='file' onChange={handleFileChange} accept='image/*' multiple />
                    </Button>
                    <Typography variant='body2' fontWeight={500} sx={{ fontSize: { xs: '12px', lg: '0.875rem' } }}>
                      No file chosen
                    </Typography>
                  </Box>
                  <Typography color='secondary' sx={{ fontSize: { xs: '11px', lg: '12px' } }}>
                    The proposed size is 350px*180px. No bigger than 2.5MB
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Grid container spacing={{ xs: 2, lg: 3 }} mt={{ xs: 2, lg: 3 }}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input
                  name='business.name'
                  label='Friendly Business Name'
                  control={control}
                  fullWidth
                  sx={{ marginTop: { xs: 1, lg: 2 } }}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input
                  name='business.legal_name'
                  label='Legal Business Name'
                  control={control}
                  fullWidth
                  sx={{ marginTop: { xs: 1, lg: 2 } }}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Select
                  name='business.industry'
                  label='Industry'
                  isLoading={loadingIndustries}
                  disabled={!agencyView}
                  control={control}
                  options={industries || []}
                  required
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input
                  name='business.primary_contact_name'
                  label='Primary Contact Person'
                  control={control}
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input name='business.email' label='Primary Contact E-mail' control={control} fullWidth required />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <TelInput
                  name='business.phone'
                  label='Business Number'
                  control={control}
                  defaultCountry='US'
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <Input
                  name='business.website'
                  label='Business Website'
                  control={control}
                  fullWidth
                  sx={{ marginY: { xs: 1, lg: 2 } }}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Business Address */}
          <Grid
            borderRadius='8px'
            size={12}
            bgcolor={theme.palette.background.paper}
            p={{ xs: 2, lg: 3 }}
            border='1px solid #ececec'
          >
            <Typography
              variant='h6'
              sx={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: { xs: '1.125rem', lg: '1.25rem' } }}
            >
              Billing Address
            </Typography>

            <Box sx={{ marginY: { xs: 1.5, lg: 2 } }}>
              <Grid container spacing={{ xs: 2, lg: 3 }}>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Input
                    name='business.address_attributes.street_address'
                    label='Street Address'
                    control={control}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Input name='business.address_attributes.city' label='City' control={control} required fullWidth />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Input
                    name='business.address_attributes.postal_code'
                    label='Postal/Zip Code'
                    control={control}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Select
                    key={businessCountry}
                    name='business.address_attributes.country'
                    label='Country'
                    isLoading={isCountriesLoading}
                    control={control}
                    options={countries || []}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, lg: 6 }}>
                  <Select
                    key={businessCountry}
                    name='business.address_attributes.state'
                    label='State / Prov / Region'
                    isLoading={isStatesLoading}
                    control={control}
                    options={states || []}
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {agencyView && (
            <Grid
              borderRadius='8px'
              size={12}
              bgcolor={theme.palette.background.paper}
              p={{ xs: 2, lg: 3 }}
              border='1px solid #ececec'
            >
              <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
                Voice Engine
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '14px', lg: 16 }, mt: 0.5 }}>
                Choose the Voice Engine that powers AI voice interactions for this business.
              </Typography>

              <Controller
                name='business.voice_engine_id'
                control={control}
                render={({ field }) => (
                  <Grid container spacing={{ xs: 1.5, lg: 2 }}>
                    {voiceEngines?.data?.map((engine) => (
                      <Grid key={engine.id} size={{ xs: 12, sm: 6, md: 4 }} mt={{ xs: 1.5, lg: 2 }}>
                        <VoiceEngineCard
                          engine={engine}
                          businessEngineVersion={
                            dirtyFields?.business?.voice_engine_id
                              ? null
                              : businessProfile?.current_voice_engine_version
                          }
                          isSelectable
                          selected={String(field.value) === String(engine?.id)}
                          onSelect={(id) => field.onChange(id)}
                          allEngines={voiceEngines?.data}
                          businessProfileUpdateContext={businessId ? { businessId: Number(businessId) } : undefined}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              />
            </Grid>
          )}

          {agencyView && (
            <Grid
              borderRadius='8px'
              size={12}
              bgcolor={theme.palette.background.paper}
              p={{ xs: 2, lg: 3 }}
              border='1px solid #ececec'
            >
              <Typography variant='h6' sx={{ fontSize: { xs: '1.125rem', lg: '1.25rem' } }}>
                Telephony Services
              </Typography>
              <Typography variant='body2' color='text.secondary' sx={{ fontSize: { xs: '14px', lg: 16 }, mt: 0.5 }}>
                Choose the telephony provider that powers calls for this business.
              </Typography>

              <Controller
                name={'business.telephony_provider_ids' as any}
                control={control}
                render={({ field }) => {
                  const activeProviders = (telephonyProviders?.data ?? [])
                    .filter((p) => p.active && p.category !== 'byoc' && p.category !== 'byob' && p.category !== 'other')
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                  const selectedIds = normalizeProviderIds(field.value);

                  const handleToggle = (id: number) => {
                    if (selectedIds.includes(id)) {
                      field.onChange(selectedIds.filter((v) => v !== id));
                    } else {
                      field.onChange([...selectedIds, id]);
                    }
                  };

                  return (
                    <Box display='flex' flexWrap='wrap' gap={1.5} mt={{ xs: 1.5, lg: 2 }}>
                      {activeProviders.length ? (
                        activeProviders.map((provider) => {
                          const isChecked = selectedIds.includes(provider.id);

                          return (
                            <Box
                              key={provider.id}
                              onClick={() => handleToggle(provider.id)}
                              sx={{
                                border: '1px solid #ECECEC',
                                borderRadius: '6px',
                                px: 1.5,
                                py: 1,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                userSelect: 'none',
                                '&:hover': {
                                  borderColor: '#D1D5DB',
                                  boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.05)'
                                }
                              }}
                            >
                              <Checkbox
                                checked={isChecked}
                                onChange={() => handleToggle(provider.id)}
                                onClick={(e) => e.stopPropagation()}
                                size='small'
                                sx={{ p: 0 }}
                              />
                              {provider.favicon_url && (
                                <Box
                                  component='img'
                                  src={provider.favicon_url}
                                  alt={provider.name}
                                  sx={{
                                    height: '30px',
                                    width: 'auto',
                                    objectFit: 'contain'
                                  }}
                                />
                              )}
                              <Typography fontSize={14} fontWeight={500} color='text.primary'>
                                {provider.friendly_name}
                              </Typography>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography
                          textAlign='center'
                          border='1px solid #ECECEC'
                          padding={4}
                          borderRadius={4}
                          width='100%'
                          color='#777777'
                        >
                          No phone providers available
                        </Typography>
                      )}
                    </Box>
                  );
                }}
              />
            </Grid>
          )}

          {/* Billing Information */}
          {agencyView && (
            <Grid
              borderRadius='8px'
              size={12}
              bgcolor={theme.palette.background.paper}
              p={{ xs: 2, lg: 3 }}
              border='1px solid #ececec'
            >
              <Typography
                variant='h6'
                sx={{ display: 'flex', alignItems: 'center', gap: '2px', fontSize: { xs: '1.125rem', lg: '1.25rem' } }}
              >
                Billing Information
              </Typography>
              <Box
                display='flex'
                flexDirection={{ xs: 'column', sm: 'row' }}
                justifyContent='space-between'
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                gap={{ xs: 2, sm: 0 }}
                sx={{
                  borderBottom: '1px solid #ececec',
                  marginTop: { xs: 1.5, lg: 2 },
                  paddingBottom: { xs: 1.5, lg: 2 }
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <Typography variant='body1' fontWeight={500} sx={{ fontSize: { xs: '1rem', lg: 18 } }}>
                    Hide billing access from business
                  </Typography>
                  <Typography
                    variant='body2'
                    color='secondary'
                    sx={{ fontSize: { xs: '14px', lg: 16 }, marginBottom: 1 }}
                  >
                    Business will not able to see billing page
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                  <Switch name='business.hide_billing' control={control} />
                </Box>
              </Box>
              <Box mt={{ xs: 2, lg: 3 }}>
                <Grid container spacing={{ xs: 2, lg: 3 }} mt={1}>
                  {plans?.data?.map((p) => (
                    <Grid key={p.id} size={{ xs: 12, md: 6 }}>
                      <PlanCard plan={p} control={control} name='business.business_plan_id' isDeleteAble={false} />
                    </Grid>
                  ))}
                </Grid>
                {isCreateMode &&
                  (errors.business as { business_plan_id?: { message?: string } } | undefined)?.business_plan_id
                    ?.message && (
                    <Typography color='error' sx={{ fontSize: { xs: '12px', lg: 14 } }} mt={1}>
                      {(errors.business as { business_plan_id?: { message?: string } }).business_plan_id?.message}
                    </Typography>
                  )}
              </Box>
            </Grid>
          )}
        </Grid>
        <Button
          color='primary'
          type='submit'
          variant='contained'
          fullWidth
          isPending={isCreating || isUpdatingProfile || isUpdatingBusiness}
          sx={{
            marginTop: { xs: '16px', lg: '24px' },
            marginLeft: { xs: 0, lg: 'auto' },
            display: 'block',
            width: { xs: '100%', lg: 'auto' }
          }}
        >
          {!isCreateMode ? 'Update' : 'Create'}
        </Button>
      </Box>
    </>
  );
}

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
});
